import { BellRingIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FieldHeader } from '@/components/atoms/FieldHeader/FieldHeader';
import {
  SegmentedChip,
  SegmentedChipLabel,
  SegmentedChipRemove,
} from '@/components/atoms/SegmentedChip/SegmentedChip';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Trigger } from '@/shared/types/rules';

import { TRIGGER_LABEL_KEYS, TRIGGERS_VALUE_TO_LABEL } from './constants';

type TriggersMenuProps = {
  isSelected: (type: string) => boolean;
  onToggle: (type: string) => void;
  defaultOpen: boolean;
};

const TriggersMenu = ({ isSelected, onToggle, defaultOpen }: TriggersMenuProps) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost-neutral" size="icon-md" aria-label={t('rules.add_trigger')}>
          <PlusIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {TRIGGERS_VALUE_TO_LABEL.map((type) => (
          <DropdownMenuItem
            key={type}
            className="gap-2"
            onSelect={(e) => {
              e.preventDefault();
              onToggle(type);
            }}
          >
            <Checkbox
              checked={isSelected(type)}
              tabIndex={-1}
              aria-hidden
              className="pointer-events-none [&_svg]:text-current!"
            />
            {t(TRIGGER_LABEL_KEYS[type])}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

type TriggersFieldProps = {
  value: Trigger[];
  onChange: (value: Trigger[]) => void;
  disabled?: boolean;
  defaultOpen?: boolean;
  error?: string;
};

export const TriggersField = ({
  value,
  onChange,
  disabled = false,
  defaultOpen = false,
  error,
}: TriggersFieldProps) => {
  const { t } = useTranslation();

  const getLabel = (type: string) =>
    TRIGGER_LABEL_KEYS[type] ? t(TRIGGER_LABEL_KEYS[type]) : type;

  const isSelected = (type: string) => value.some((trigger) => trigger.type === type);

  const toggle = (type: string) => {
    if (isSelected(type)) {
      onChange(value.filter((trigger) => trigger.type !== type));
    } else {
      onChange([...value, { type }]);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <FieldHeader
        title={t('rules.editor.trigger_title')}
        description={t('rules.editor.trigger_description')}
        error={error}
        action={
          !disabled && (
            <TriggersMenu isSelected={isSelected} onToggle={toggle} defaultOpen={defaultOpen} />
          )
        }
      />
      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {value.map((trigger) => (
            <SegmentedChip key={trigger.type}>
              <SegmentedChipLabel icon={BellRingIcon}>{getLabel(trigger.type)}</SegmentedChipLabel>
              {!disabled && (
                <SegmentedChipRemove
                  label={getLabel(trigger.type)}
                  onClick={() => toggle(trigger.type)}
                />
              )}
            </SegmentedChip>
          ))}
        </div>
      )}
    </div>
  );
};
